import ImageUploadInput from "@/components/ImageUploadInput";
import TebexSyncModal from "@/components/TebexSyncModal";
import LatestReleaseTebexPicker from "@/components/LatestReleaseTebexPicker";
import StorageMeter from "@/components/StorageMeter";
import AdminRaffleTab from "@/components/AdminRaffleTab";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { fetchConfig, updateConfig, resetConfig, me, getToken, clearToken, generateCfxKeys, fetchOrders, refreshOrderFromTebex, deleteOrder, syncAllOrders } from "@/api";
import { toast } from "sonner";
import { LogOut, Save, RotateCcw, Plus, Trash2, ArrowLeft, ExternalLink, KeyRound, Copy, RefreshCw, Palette, Check } from "lucide-react";

// Discount preview math (matches ProductsSection logic)
function applyDiscountPreview(priceStr, pct) {
  if (!priceStr || !pct) return priceStr;
  return priceStr.replace(/(\d+(?:\.\d+)?)/g, (m) => {
    const v = parseFloat(m) * (1 - pct / 100);
    return m.includes(".") ? v.toFixed(2) : String(Math.round(v));
  });
}

const Field = ({ label, value, onChange, type = "text", placeholder, testid, mono = true }) => (
  <div>
    <label className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] block mb-2">
      &gt; {label}
    </label>
    {type === "textarea" ? (
      <textarea
        data-testid={testid}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        rows={3}
        className={`w-full bg-black border border-[var(--line-strong)] focus:border-[var(--accent)] outline-none px-3 py-2 text-sm text-white ${mono ? "font-mono" : ""}`}
      />
    ) : (
      <input
        data-testid={testid}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className={`w-full bg-black border border-[var(--line-strong)] focus:border-[var(--accent)] outline-none px-3 py-2 text-sm text-white ${mono ? "font-mono" : ""}`}
      />
    )}
  </div>
);

/**
 * SwatchInput — A round color-picker swatch + hex input pair for the Theme tab.
 * Handles both #RRGGBB hex (via <input type=color>) and arbitrary rgba() strings
 * (via the freeform text input). The colour picker's `value` always tries to
 * coerce the current value to a 7-char hex so the native picker doesn't reject it.
 */
function SwatchInput({ label, value, onChange, testid }) {
  const hexMatch = (value || "").trim().match(/^#?[a-fA-F0-9]{6}$/);
  const colorPreview = hexMatch ? (value.startsWith("#") ? value : `#${value}`) : "#000000";
  return (
    <div className="flex flex-col items-center gap-1.5 flex-1 min-w-0">
      <label className="relative w-9 h-9 border border-[var(--theme-border)] cursor-pointer overflow-hidden" title={label}>
        <input
          data-testid={testid}
          type="color"
          value={colorPreview}
          onChange={(e) => onChange(e.target.value.toUpperCase())}
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        />
        <span
          className="block w-full h-full"
          style={{ background: value }}
        />
      </label>
      <span className="font-mono text-[8px] uppercase tracking-widest text-[var(--theme-text-muted)] truncate w-full text-center">
        {label}
      </span>
    </div>
  );
}

export default function AdminDashboard({ onThemeChange = () => {} }) {
  const navigate = useNavigate();
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [tab, setTab] = useState("settings");
  const [adminEmail, setAdminEmail] = useState("");

  useEffect(() => {
    if (!getToken()) {
      navigate("/admin");
      return;
    }
    // Only invalidate the token if /auth/me fails — that's a real auth problem.
    // A transient /config failure shouldn't log the admin out; retry instead.
    me()
      .then((user) => {
        setAdminEmail(user.email);
        return fetchConfig().then((cfg) => setConfig(cfg));
      })
      .catch((err) => {
        const status = err.response?.status;
        if (status === 401 || status === 403) {
          clearToken();
          navigate("/admin");
        } else {
          // Network blip / DB transient — surface the error but keep the session.
          toast.error("Could not load config — retrying in 3s...");
          setTimeout(() => {
            fetchConfig().then(setConfig).catch(() => {
              toast.error("Still offline. Refresh the page when ready.");
            });
          }, 3000);
        }
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  const logout = () => {
    clearToken();
    navigate("/admin");
  };

  const save = async () => {
    setSaving(true);
    try {
      const updated = await updateConfig(config);
      setConfig(updated);
      onThemeChange(updated?.theme || null);
      toast.success("Configuration saved.");
    } catch (e) {
      if (e.response?.status === 401) {
        clearToken();
        navigate("/admin");
        return;
      }
      toast.error("Save failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm("Reset all pricing to defaults? This overwrites current data.")) return;
    try {
      const fresh = await resetConfig();
      setConfig(fresh);
      toast.success("Pricing reset to defaults.");
    } catch (e) {
      toast.error("Reset failed");
    }
  };

  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [refreshingOrders, setRefreshingOrders] = useState({});
  const refreshOrders = async () => {
    setOrdersLoading(true);
    try {
      const list = await fetchOrders();
      setOrders(list);
    } catch (e) {
      toast.error("Failed to load orders");
    } finally {
      setOrdersLoading(false);
    }
  };
  const refreshOneOrder = async (orderId) => {
    setRefreshingOrders((s) => ({ ...s, [orderId]: true }));
    try {
      const data = await refreshOrderFromTebex(orderId);
      setOrders((curr) => curr.map((o) => (o.id === orderId ? data.order : o)));
      const newStatus = data.order?.status || "—";
      toast.success(`Synced from Tebex — status: ${newStatus.replace(/_/g, " ").toUpperCase()}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally {
      setRefreshingOrders((s) => ({ ...s, [orderId]: false }));
    }
  };
  const deleteOneOrder = async (orderId) => {
    if (!window.confirm("Delete this order from the dashboard? (does not affect Tebex)")) return;
    try {
      await deleteOrder(orderId);
      setOrders((curr) => curr.filter((o) => o.id !== orderId));
      toast.success("Order removed");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };
  const [syncingAll, setSyncingAll] = useState(false);
  const handleSyncAll = async () => {
    setSyncingAll(true);
    try {
      const data = await syncAllOrders();
      toast.success(
        `Checked ${data.checked} · paid ${data.paid} · cancelled ${data.cancelled} · still open ${data.still_open}`
      );
      await refreshOrders();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Sync failed");
    } finally {
      setSyncingAll(false);
    }
  };
  useEffect(() => {
    if (tab === "orders") refreshOrders();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab]);

  const regenCfxKeys = async () => {
    if (!window.confirm("Generate new RSA keys for CFX login? Existing in-flight logins will fail.")) return;
    try {
      const data = await generateCfxKeys();
      const fresh = await fetchConfig();
      setConfig(fresh);
      toast.success(`New CFX keys generated. Client ID: ${data.cfx_client_id}`);
    } catch (e) {
      toast.error("Failed to generate keys: " + (e.response?.data?.detail || e.message));
    }
  };

  // === Tebex Storefront Sync ===
  const [tebexSyncOpen, setTebexSyncOpen] = useState(false);
  const [latestReleasePickerOpen, setLatestReleasePickerOpen] = useState(false);
  const openTebexSync = () => {
    if (!config.settings.tebex_public_token || !config.settings.tebex_private_key) {
      toast.error("Add your Tebex Public Token + Private Key in the CFX + TEBEX tab first");
      setTab("integrations");
      return;
    }
    setTebexSyncOpen(true);
  };
  const importTebexPackages = (picks, targetCategoryId) => {
    // picks = [{ id, name, description, image, price, category, type }]
    const cats = [...(config.product_categories || [])];
    let targetIdx = cats.findIndex((c) => c.id === targetCategoryId);
    if (targetIdx === -1) {
      // Create a new category named "From Tebex"
      const newCat = {
        id: crypto.randomUUID(),
        name: "From Tebex",
        order: cats.length,
        products: [],
      };
      cats.push(newCat);
      targetIdx = cats.length - 1;
    }
    const existingIds = new Set(
      (cats[targetIdx].products || []).map((p) => String(p.tebex_package_id || ""))
    );
    let added = 0;
    let skipped = 0;
    picks.forEach((pkg) => {
      const idStr = String(pkg.id);
      if (existingIds.has(idStr)) {
        skipped += 1;
        return;
      }
      cats[targetIdx].products.push({
        id: crypto.randomUUID(),
        title: pkg.name || `Package ${pkg.id}`,
        description: pkg.description || "",
        images: pkg.image ? [{ id: crypto.randomUUID(), url: pkg.image, caption: "" }] : [],
        purchase_url: "",
        price: pkg.price || "",
        badge: "",
        discount: 0,
        sale_label: "",
        order: (cats[targetIdx].products || []).length,
        tebex_package_id: idStr,
      });
      added += 1;
    });
    setConfig({ ...config, product_categories: cats });
    setTebexSyncOpen(false);
    toast.success(
      `Imported ${added} package${added === 1 ? "" : "s"}${skipped ? ` · skipped ${skipped} already linked` : ""} — review and SAVE`
    );
  };

  if (loading || !config) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center px-6">
        <div className="flex flex-col items-center gap-6 text-center">
          <img src="/logo.png?v=4" alt="" className="h-14 w-14 object-contain drop-shadow-[0_0_18px_rgba(var(--theme-accent-primary-rgb),0.45)] animate-pulse" />
          <div className="font-display text-2xl md:text-3xl font-extrabold uppercase tracking-[0.18em] text-[var(--accent)] text-glow">
            NULL // MODIFICATIONS
          </div>
          <div className="font-mono text-[11px] tracking-[0.4em] text-[var(--muted)] uppercase cursor-blink">
            &gt; UNLOCKING DASHBOARD
          </div>
        </div>
      </div>
    );
  }

  const updateTier = (idx, patch) => {
    const tiers = [...config.tiers];
    tiers[idx] = { ...tiers[idx], ...patch };
    setConfig({ ...config, tiers });
  };

  const updateFeature = (tierIdx, fIdx, patch) => {
    const tiers = [...config.tiers];
    const features = [...tiers[tierIdx].features];
    features[fIdx] = { ...features[fIdx], ...patch };
    tiers[tierIdx] = { ...tiers[tierIdx], features };
    setConfig({ ...config, tiers });
  };

  const addFeature = (tierIdx) => {
    const tiers = [...config.tiers];
    tiers[tierIdx] = {
      ...tiers[tierIdx],
      features: [...tiers[tierIdx].features, { ok: true, text: "New feature" }],
    };
    setConfig({ ...config, tiers });
  };

  const removeFeature = (tierIdx, fIdx) => {
    const tiers = [...config.tiers];
    tiers[tierIdx] = {
      ...tiers[tierIdx],
      features: tiers[tierIdx].features.filter((_, i) => i !== fIdx),
    };
    setConfig({ ...config, tiers });
  };

  const updateBulk = (idx, patch) => {
    const bulk = [...config.bulk];
    bulk[idx] = { ...bulk[idx], ...patch };
    setConfig({ ...config, bulk });
  };

  const updateAddon = (idx, patch) => {
    const addons = [...config.addons];
    addons[idx] = { ...addons[idx], ...patch };
    setConfig({ ...config, addons });
  };

  // ---- Product Category helpers ----
  const product_categories = config.product_categories || [];

  const addProductCategory = () => {
    setConfig({
      ...config,
      product_categories: [
        ...product_categories,
        {
          id: `pcat-${Date.now()}`,
          name: "New Category",
          slug: `cat-${Date.now()}`,
          description: "",
          products: [],
          order: product_categories.length + 1,
        },
      ],
    });
  };

  const updateProductCategory = (idx, patch) => {
    const next = [...product_categories];
    next[idx] = { ...next[idx], ...patch };
    setConfig({ ...config, product_categories: next });
  };

  const removeProductCategory = (idx) => {
    if (!window.confirm("Delete this entire category and all its products?")) return;
    setConfig({
      ...config,
      product_categories: product_categories.filter((_, i) => i !== idx),
    });
  };

  const moveProductCategory = (idx, dir) => {
    const next = [...product_categories];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    next.forEach((c, i) => { c.order = i + 1; });
    setConfig({ ...config, product_categories: next });
  };

  const addProduct = (catIdx) => {
    const cat = product_categories[catIdx];
    const products = [...(cat.products || []), {
      id: `prod-${Date.now()}`,
      title: "New Product",
      description: "",
      images: [],
      purchase_url: "",
      price: "",
      badge: "",
      order: (cat.products?.length || 0) + 1,
    }];
    updateProductCategory(catIdx, { products });
  };

  const updateProduct = (catIdx, prodIdx, patch) => {
    const cat = product_categories[catIdx];
    const products = [...cat.products];
    products[prodIdx] = { ...products[prodIdx], ...patch };
    updateProductCategory(catIdx, { products });
  };

  const removeProduct = (catIdx, prodIdx) => {
    if (!window.confirm("Delete this product?")) return;
    const cat = product_categories[catIdx];
    updateProductCategory(catIdx, {
      products: cat.products.filter((_, i) => i !== prodIdx),
    });
  };

  const addProductImage = (catIdx, prodIdx) => {
    const prod = product_categories[catIdx].products[prodIdx];
    const images = [...(prod.images || []), {
      id: `pimg-${Date.now()}`, url: "", caption: "", order: (prod.images?.length || 0) + 1,
    }];
    updateProduct(catIdx, prodIdx, { images });
  };

  const updateProductImage = (catIdx, prodIdx, imgIdx, patch) => {
    const prod = product_categories[catIdx].products[prodIdx];
    const images = [...prod.images];
    images[imgIdx] = { ...images[imgIdx], ...patch };
    updateProduct(catIdx, prodIdx, { images });
  };

  const removeProductImage = (catIdx, prodIdx, imgIdx) => {
    const prod = product_categories[catIdx].products[prodIdx];
    updateProduct(catIdx, prodIdx, {
      images: prod.images.filter((_, i) => i !== imgIdx),
    });
  };

  // ---- Portfolio helpers ----
  const portfolio = config.portfolio || [];

  const addCategory = () => {
    const nextOrder = portfolio.length + 1;
    setConfig({
      ...config,
      portfolio: [
        ...portfolio,
        {
          id: `cat-${Date.now()}`,
          name: "New Build",
          description: "",
          thumbnail: "",
          images: [],
          order: nextOrder,
        },
      ],
    });
  };

  const updateCategory = (idx, patch) => {
    const next = [...portfolio];
    next[idx] = { ...next[idx], ...patch };
    setConfig({ ...config, portfolio: next });
  };

  const removeCategory = (idx) => {
    if (!window.confirm("Delete this build and all its images?")) return;
    const next = portfolio.filter((_, i) => i !== idx);
    setConfig({ ...config, portfolio: next });
  };

  const moveCategory = (idx, dir) => {
    const next = [...portfolio];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    next.forEach((c, i) => { c.order = i + 1; });
    setConfig({ ...config, portfolio: next });
  };

  const addImageToCategory = (catIdx) => {
    const cat = portfolio[catIdx];
    const images = [...(cat.images || []), {
      id: `img-${Date.now()}`,
      url: "",
      caption: "",
      order: (cat.images?.length || 0) + 1,
    }];
    updateCategory(catIdx, { images });
  };

  const updateImage = (catIdx, imgIdx, patch) => {
    const cat = portfolio[catIdx];
    const images = [...cat.images];
    images[imgIdx] = { ...images[imgIdx], ...patch };
    updateCategory(catIdx, { images });
  };

  const removeImage = (catIdx, imgIdx) => {
    const cat = portfolio[catIdx];
    const images = cat.images.filter((_, i) => i !== imgIdx);
    updateCategory(catIdx, { images });
  };

  const updateSetting = (patch) => {
    setConfig({ ...config, settings: { ...config.settings, ...patch } });
  };

  // ---- Theme engine helpers ----
  const theme = config.theme || { active_preset_id: "default_bw", presets: [] };
  const themePresets = theme.presets || [];
  const activeThemeId = theme.active_preset_id;
  const setTheme = (patch) => {
    setConfig({ ...config, theme: { ...theme, ...patch } });
    // Live-preview into the page immediately (also persisted on Save)
    try {
      const next = { ...theme, ...patch };
      const active =
        (next.presets || []).find((p) => p.id === next.active_preset_id) ||
        (next.presets || [])[0];
      if (active) {
        import("@/context/ThemeContext").then((m) => m.applyTheme(active));
      }
    } catch (_) { /* ignore */ }
  };
  const updatePreset = (presetId, patch) => {
    const presets = themePresets.map((p) =>
      p.id === presetId ? { ...p, ...patch } : p
    );
    setTheme({ presets });
  };
  const addPreset = () => {
    const id = "preset-" + Math.random().toString(36).slice(2, 8);
    const fresh = {
      id,
      name: "New Preset",
      bg: "#050505",
      surface: "#121212",
      border: "rgba(255,255,255,0.10)",
      accent_primary: "#FFFFFF",
      accent_secondary: "#A3A3A3",
      text: "#FFFFFF",
      text_muted: "#A1A1AA",
      locked: false,
    };
    setTheme({ presets: [...themePresets, fresh] });
  };
  const deletePreset = (presetId) => {
    const p = themePresets.find((x) => x.id === presetId);
    if (!p) return;
    if (p.locked) {
      toast.error("Built-in preset cannot be deleted.");
      return;
    }
    if (!window.confirm(`Delete preset "${p.name}"?`)) return;
    const presets = themePresets.filter((x) => x.id !== presetId);
    const nextActive =
      activeThemeId === presetId ? (presets[0]?.id || "default_bw") : activeThemeId;
    setTheme({ presets, active_preset_id: nextActive });
  };
  const activatePreset = (presetId) => setTheme({ active_preset_id: presetId });

  const TABS = [
    { id: "settings", label: "Settings" },
    { id: "integrations", label: "CFX + Tebex" },
    { id: "theme", label: "Theme" },
    { id: "tiers", label: "Pricing Tiers" },
    { id: "products", label: "Products" },
    { id: "orders", label: "Orders" },
    { id: "portfolio", label: "Portfolio" },
    { id: "raffle", label: "Raffle" },
    { id: "tos", label: "Terms" },
  ];

  return (
    <div className="min-h-screen bg-black text-white">
      {/* Header */}
      <header className="bg-[#0a0a0a] border-b border-[var(--line-strong)] sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <button
              onClick={() => navigate("/")}
              data-testid="back-to-site"
              className="font-mono text-xs uppercase tracking-widest text-[var(--muted)] hover:text-[var(--accent)] flex items-center gap-2"
            >
              <ArrowLeft size={14} /> SITE
            </button>
            <span className="text-[var(--line-strong)]">|</span>
            <div>
              <div className="bracket">[ ADMIN_PANEL ]</div>
              <div className="font-display font-extrabold uppercase tracking-tight text-lg">
                NULL // CONTROL
              </div>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <div className="hidden md:block font-mono text-[11px] text-[var(--muted)]">
              <span className="text-[var(--accent)]">●</span> {adminEmail}
            </div>
            <button
              data-testid="save-btn"
              onClick={save}
              disabled={saving}
              className="btn-accent !py-2 !px-3 !text-[11px] disabled:opacity-50"
            >
              <Save size={12} /> {saving ? "SAVING..." : "SAVE"}
            </button>
            <button
              data-testid="logout-btn"
              onClick={logout}
              className="btn-ghost !py-2 !px-3 !text-[11px]"
            >
              <LogOut size={12} /> LOGOUT
            </button>
          </div>
        </div>

        {/* Tabs */}
        <div className="max-w-7xl mx-auto px-6 flex gap-1 overflow-x-auto">
          {TABS.map((t) => (
            <button
              key={t.id}
              data-testid={`tab-${t.id}`}
              onClick={() => setTab(t.id)}
              className={`font-mono text-xs uppercase tracking-widest px-4 py-3 border-b-2 transition whitespace-nowrap ${
                tab === t.id
                  ? "border-[var(--accent)] text-[var(--accent)]"
                  : "border-transparent text-[var(--muted)] hover:text-white"
              }`}
            >
              // {t.label}
            </button>
          ))}
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        {/* SETTINGS */}
        {tab === "settings" && (
          <section className="space-y-6 max-w-2xl" data-testid="panel-settings">
            <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight mb-2">
              Site Settings
            </h2>

            {/* Storage meter — live MongoDB + GridFS usage */}
            <StorageMeter />

            <div className="bg-[#0a0a0a] border border-[var(--line-strong)] p-6 space-y-5">
              <Field
                label="Tebex URL"
                value={config.settings.tebex_url}
                onChange={(v) => updateSetting({ tebex_url: v })}
                placeholder="https://yourshop.tebex.io"
                testid="setting-tebex"
              />
              <Field
                label="Discord Invite URL"
                value={config.settings.discord_url}
                onChange={(v) => updateSetting({ discord_url: v })}
                placeholder="https://discord.gg/..."
                testid="setting-discord"
              />
              <Field
                label="Support Email (public)"
                value={config.settings.support_email || ""}
                onChange={(v) => updateSetting({ support_email: v })}
                placeholder="support@yourdomain.com"
                testid="setting-support-email"
                type="email"
              />
              <Field
                label="Background Music — fallback single YouTube URL (used if queue empty)"
                value={config.settings.youtube_url || ""}
                onChange={(v) => updateSetting({ youtube_url: v })}
                placeholder="https://www.youtube.com/watch?v=..."
                testid="setting-youtube-url"
              />

              {/* Queue editor */}
              <div className="pt-4 border-t border-[var(--line)]">
                <div className="flex items-center justify-between mb-3">
                  <label className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">
                    &gt; music queue ({(config.settings.youtube_queue || []).length})
                  </label>
                  <button
                    data-testid="add-queue-track"
                    onClick={() => {
                      const q = [...(config.settings.youtube_queue || []), ""];
                      updateSetting({ youtube_queue: q });
                    }}
                    className="font-mono text-[10px] uppercase tracking-widest text-[var(--accent)] hover:underline flex items-center gap-1"
                  >
                    <Plus size={10} /> ADD TRACK
                  </button>
                </div>
                <div className="space-y-2">
                  {(config.settings.youtube_queue || []).map((u, qi) => (
                    <div key={qi} data-testid={`queue-row-${qi}`} className="flex items-center gap-2">
                      <span className="font-mono text-[10px] text-[var(--accent)] tracking-widest w-7">
                        #{String(qi + 1).padStart(2, "0")}
                      </span>
                      <input
                        value={u}
                        onChange={(e) => {
                          const q = [...config.settings.youtube_queue];
                          q[qi] = e.target.value;
                          updateSetting({ youtube_queue: q });
                        }}
                        placeholder="https://www.youtube.com/watch?v=..."
                        className="flex-1 bg-black border border-[var(--line-strong)] focus:border-[var(--accent)] outline-none px-2 py-2 text-xs font-mono"
                      />
                      <button
                        onClick={() => {
                          const q = [...config.settings.youtube_queue];
                          if (qi === 0) return;
                          [q[qi - 1], q[qi]] = [q[qi], q[qi - 1]];
                          updateSetting({ youtube_queue: q });
                        }}
                        disabled={qi === 0}
                        className="border border-[var(--line-strong)] hover:border-[var(--accent)] px-2 py-1 text-xs font-mono disabled:opacity-30"
                        aria-label="move up"
                      >↑</button>
                      <button
                        onClick={() => {
                          const q = [...config.settings.youtube_queue];
                          if (qi === q.length - 1) return;
                          [q[qi + 1], q[qi]] = [q[qi], q[qi + 1]];
                          updateSetting({ youtube_queue: q });
                        }}
                        disabled={qi === (config.settings.youtube_queue.length - 1)}
                        className="border border-[var(--line-strong)] hover:border-[var(--accent)] px-2 py-1 text-xs font-mono disabled:opacity-30"
                        aria-label="move down"
                      >↓</button>
                      <button
                        onClick={() => {
                          const q = (config.settings.youtube_queue || []).filter((_, k) => k !== qi);
                          updateSetting({ youtube_queue: q });
                        }}
                        className="border border-[var(--line-strong)] hover:border-red-500 hover:text-red-500 p-2"
                        aria-label="remove"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  ))}
                </div>
                <p className="font-mono text-[10px] text-[var(--muted)] mt-3 leading-relaxed">
                  // tracks play in order. when the last one ends, the queue loops from the top.<br />
                  // if the queue is empty, the fallback single URL above is used.
                </p>
              </div>

              {/* === LIVE STATS (Discord + GTA5-Mods) === */}
              <div className="border border-[var(--accent)]/30 bg-black/40 p-5 space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="bracket text-[var(--accent)]">[ LIVE_STATS_FEED ]</div>
                    <h3 className="font-display text-lg font-bold uppercase tracking-tight mt-0.5">
                      Auto-Updated Hero Stats
                    </h3>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      data-testid="setting-livestats-enabled"
                      type="checkbox"
                      checked={!!config.settings.live_stats?.enabled}
                      onChange={(e) => updateSetting({
                        live_stats: { ...(config.settings.live_stats || {}), enabled: e.target.checked },
                      })}
                      className="w-4 h-4 accent-[var(--accent)] cursor-pointer"
                    />
                    <span className="font-mono text-[10px] uppercase tracking-widest">Enabled</span>
                  </label>
                </div>

                <p className="font-mono text-[10px] text-[var(--muted)] leading-relaxed">
                  // When enabled, the manual stat blocks on the right of the hero are
                  replaced with two live, clickable cards — Discord (members + online)
                  and GTA5-Mods (releases + downloads). Refreshed every 10 minutes server-side.
                </p>

                <Field
                  label="Discord Invite Code (or full invite URL)"
                  value={config.settings.live_stats?.discord_invite_code || ""}
                  onChange={(v) => updateSetting({
                    live_stats: { ...(config.settings.live_stats || {}), discord_invite_code: v },
                  })}
                  placeholder="e.g. XZNMfWekDz  or  https://discord.gg/XZNMfWekDz"
                  testid="setting-livestats-discord"
                />
                <Field
                  label="GTA5-Mods Username"
                  value={config.settings.live_stats?.gta5mods_username || ""}
                  onChange={(v) => updateSetting({
                    live_stats: { ...(config.settings.live_stats || {}), gta5mods_username: v },
                  })}
                  placeholder="e.g. NullModifications"
                  testid="setting-livestats-gta"
                />
              </div>

              {/* === LATEST RELEASE CARD === */}
              <div className="border border-[var(--accent)]/30 bg-black/40 p-5 space-y-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <div className="bracket text-[var(--accent)]">[ LATEST_RELEASE ]</div>
                    <h3 className="font-display text-lg font-bold uppercase tracking-tight mt-0.5">
                      Featured Release Card
                    </h3>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      data-testid="setting-latestrelease-enabled"
                      type="checkbox"
                      checked={!!config.settings.latest_release?.enabled}
                      onChange={(e) => updateSetting({
                        latest_release: { ...(config.settings.latest_release || {}), enabled: e.target.checked },
                      })}
                      className="w-4 h-4 accent-[var(--accent)] cursor-pointer"
                    />
                    <span className="font-mono text-[10px] uppercase tracking-widest">Enabled</span>
                  </label>
                </div>

                <p className="font-mono text-[10px] text-[var(--muted)] leading-relaxed">
                  // shown directly under the hero stats cards. pick FREE for a glowing green
                  "DOWNLOAD NOW" button linked to GTA5-Mods, or PAID for a "PURCHASE NOW"
                  button linked to your Tebex product page.
                </p>

                <Field
                  label="Title"
                  value={config.settings.latest_release?.title || ""}
                  onChange={(v) => updateSetting({
                    latest_release: { ...(config.settings.latest_release || {}), title: v },
                  })}
                  placeholder="e.g. 2024 Aventador SVJ — Realistic Pack"
                  testid="setting-latestrelease-title"
                />
                <Field
                  label="Description"
                  type="textarea"
                  value={config.settings.latest_release?.description || ""}
                  onChange={(v) => updateSetting({
                    latest_release: { ...(config.settings.latest_release || {}), description: v },
                  })}
                  placeholder="Short one-liner shown under the title"
                  testid="setting-latestrelease-description"
                />

                <div>
                  <label className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] block mb-2">
                    &gt; Thumbnail (external image URL — Imgur / Postimg / etc.)
                  </label>
                  <ImageUploadInput
                    value={config.settings.latest_release?.image || ""}
                    onChange={(v) => updateSetting({
                      latest_release: { ...(config.settings.latest_release || {}), image: v },
                    })}
                    placeholder="https://i.imgur.com/your-thumb.jpg"
                    testid="setting-latestrelease-image"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] block mb-2">
                      &gt; Release Type
                    </label>
                    <select
                      data-testid="setting-latestrelease-type"
                      value={config.settings.latest_release?.release_type || "free"}
                      onChange={(e) => updateSetting({
                        latest_release: { ...(config.settings.latest_release || {}), release_type: e.target.value },
                      })}
                      className="w-full bg-black border border-[var(--line-strong)] focus:border-[var(--accent)] outline-none px-3 py-2 text-sm text-white font-mono uppercase tracking-widest"
                    >
                      <option value="free">FREE — GTA5-Mods</option>
                      <option value="paid">PAID — Tebex</option>
                    </select>
                  </div>
                  <Field
                    label="Platform Label (badge text)"
                    value={config.settings.latest_release?.platform || ""}
                    onChange={(v) => updateSetting({
                      latest_release: { ...(config.settings.latest_release || {}), platform: v },
                    })}
                    placeholder="GTA5-MODS / TEBEX / FIVEM"
                    testid="setting-latestrelease-platform"
                  />
                </div>

                <Field
                  label="Button Label Override (optional)"
                  value={config.settings.latest_release?.button_label || ""}
                  onChange={(v) => updateSetting({
                    latest_release: { ...(config.settings.latest_release || {}), button_label: v },
                  })}
                  placeholder="defaults: DOWNLOAD NOW (free) / PURCHASE NOW (paid)"
                  testid="setting-latestrelease-button"
                />

                {/* === Type-specific link UI === */}
                {(config.settings.latest_release?.release_type || "free") === "free" ? (
                  <Field
                    label="GTA5-Mods Download URL"
                    value={config.settings.latest_release?.link || ""}
                    onChange={(v) => updateSetting({
                      latest_release: { ...(config.settings.latest_release || {}), link: v },
                    })}
                    placeholder="https://www.gta5-mods.com/vehicles/..."
                    testid="setting-latestrelease-link"
                  />
                ) : (
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] block mb-2">
                      &gt; Linked Tebex Package
                    </label>
                    <div className="flex items-stretch gap-2">
                      <div className="flex-1 bg-black border border-[var(--line-strong)] px-3 py-2 font-mono text-sm text-white truncate">
                        {config.settings.latest_release?.tebex_package_id ? (
                          <span className="text-[var(--accent)]">
                            ID: {config.settings.latest_release.tebex_package_id}
                          </span>
                        ) : (
                          <span className="text-[var(--muted)]">// no package linked yet</span>
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          if (!config.settings.tebex_public_token || !config.settings.tebex_private_key) {
                            toast.error("Add your Tebex Public Token + Private Key in the CFX + TEBEX tab first");
                            setTab("integrations");
                            return;
                          }
                          setLatestReleasePickerOpen(true);
                        }}
                        className="px-4 py-2 border border-[var(--accent)] bg-[var(--accent)] text-black font-mono text-[10px] uppercase tracking-widest font-bold hover:bg-white transition whitespace-nowrap"
                        data-testid="setting-latestrelease-pick-tebex"
                      >
                        Sync from Tebex
                      </button>
                    </div>
                    {config.settings.latest_release?.tebex_package_id && (
                      <button
                        type="button"
                        onClick={() => updateSetting({
                          latest_release: { ...(config.settings.latest_release || {}), tebex_package_id: "" },
                        })}
                        className="mt-2 font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] hover:text-red-400 transition"
                        data-testid="setting-latestrelease-unlink-tebex"
                      >
                        // unlink package
                      </button>
                    )}
                    <p className="font-mono text-[10px] text-[var(--muted)] mt-2 leading-relaxed">
                      // when a customer clicks PURCHASE NOW, a Tebex basket is created
                      for this package and they're redirected through your cart's normal
                      checkout flow (CFX login + Tebex FiveM auth if required).
                    </p>
                  </div>
                )}
              </div>

              <p className="font-mono text-[10px] text-[var(--muted)] -mt-3">
                // visitors get a floating player with thumbnail + progress bar + volume. starts muted (browser policy) — auto-enables on first click/scroll.
              </p>
              <Field
                label="Hero Tagline"
                value={config.settings.tagline}
                onChange={(v) => updateSetting({ tagline: v })}
                testid="setting-tagline"
                mono={false}
              />

              {/* Site-wide Discord / Twitter / Google link-preview thumbnail.
                  Takes priority over the Latest Release image so every page
                  gets a consistent brand thumbnail (default: white logo). */}
              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] block mb-2">
                  &gt; Website Thumbnail (Discord / Twitter / Google link previews)
                </label>
                <ImageUploadInput
                  value={config.settings.site_thumbnail || ""}
                  onChange={(v) => updateSetting({ site_thumbnail: v })}
                  accept="image/*"
                  placeholder="upload your white logo — used as every link preview thumbnail"
                  testid="setting-site-thumbnail"
                />
                <p className="font-mono text-[10px] text-[var(--muted)] mt-2 leading-relaxed">
                  // this image shows when someone pastes ANY nullmodifications.com link on Discord / Twitter / Google. Overrides the Latest Release image. Leave empty to fall back to the Latest Release image, then to /logo.png. Discord caches previews for ~24h so changes take a day to appear on old messages.
                </p>
                {config.settings.site_thumbnail && (
                  <div className="mt-3 border border-[var(--line-strong)] w-40 h-24 overflow-hidden bg-black">
                    <img
                      src={config.settings.site_thumbnail}
                      alt="site thumbnail preview"
                      className="w-full h-full object-contain"
                      data-testid="setting-site-thumbnail-preview"
                    />
                  </div>
                )}
              </div>
              {/* === IMAGE UPLOAD INSTRUCTIONS === */}
              <div className="border border-[var(--accent)]/20 bg-[var(--accent)]/5 p-4 font-mono text-[11px] text-[var(--muted)] leading-relaxed space-y-1">
                <div className="text-[var(--accent)] font-bold uppercase tracking-widest mb-1">// HOW TO ADD IMAGES</div>
                <p>1. <span className="text-white">Drag a PNG/JPG/WEBP/GIF</span> from your computer into any upload zone — or click to pick one.</p>
                <p>2. Or click <span className="text-[var(--accent)]">IMPORT URL</span> to paste any imgur / postimg / imgbb / discord link — the image will be downloaded and re-hosted on your site automatically.</p>
                <p>3. Files are stored permanently in your site database. URLs <span className="text-white">never expire</span>.</p>
                <p>4. Max <span className="text-white">25 MB</span> per file. Hero supports MP4/WEBM video.</p>
                <p className="pt-1 text-red-400">⚠ Don't paste raw imgur/discord URLs directly — they break or expire. Use IMPORT URL instead, it converts them to permanent links.</p>
              </div>

              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] block mb-2">
                  &gt; Hero Background (image / gif / mp4 / webm)
                </label>
                <ImageUploadInput
                  value={config.settings.hero_bg || ""}
                  onChange={(v) => updateSetting({ hero_bg: v })}
                  accept="image/*,video/mp4,video/webm,video/quicktime"
                  placeholder="https://...image.png  or  https://...clip.mp4 (or upload above)"
                  testid="setting-hero-bg"
                />
              </div>
              {config.settings.hero_bg && (
                <div className="border border-[var(--line-strong)] aspect-video overflow-hidden bg-black">
                  {(() => {
                    const u = (config.settings.hero_bg || "").split("?")[0].split("#")[0].toLowerCase();
                    const isVideo = /\.(mp4|webm|mov|m4v|ogv)$/.test(u);
                    return isVideo ? (
                      <video
                        key={config.settings.hero_bg}
                        src={config.settings.hero_bg}
                        className="w-full h-full object-cover"
                        autoPlay
                        loop
                        muted
                        playsInline
                      />
                    ) : (
                      <img src={config.settings.hero_bg} alt="hero preview" className="w-full h-full object-cover" />
                    );
                  })()}
                </div>
              )}

              {/* Hero Stats editor */}
              <div className="pt-4 border-t border-[var(--line)]">
                <div className="flex items-center justify-between mb-3">
                  <label className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">
                    &gt; hero stats (right-side metric block)
                  </label>
                  <button
                    data-testid="add-stat"
                    onClick={() => {
                      const stats = [...(config.settings.stats || []), {
                        id: `s${Date.now()}`,
                        label: "METRIC_XX",
                        value: "0",
                        sublabel: "label here",
                        order: (config.settings.stats?.length || 0) + 1,
                      }];
                      updateSetting({ stats });
                    }}
                    className="font-mono text-[10px] uppercase tracking-widest text-[var(--accent)] hover:underline flex items-center gap-1"
                  >
                    <Plus size={10} /> ADD STAT
                  </button>
                </div>
                <div className="space-y-3">
                  {(config.settings.stats || []).map((s, i) => (
                    <div key={s.id} data-testid={`stat-row-${i}`} className="border border-[var(--line-strong)] p-3 space-y-2">
                      <div className="flex items-center justify-between">
                        <div className="font-mono text-[10px] text-[var(--accent)] tracking-widest">
                          STAT #{i + 1}
                        </div>
                        <button
                          onClick={() => {
                            const stats = config.settings.stats.filter((_, k) => k !== i);
                            updateSetting({ stats });
                          }}
                          className="border border-[var(--line-strong)] hover:border-red-500 hover:text-red-500 p-1.5"
                          aria-label="remove stat"
                        >
                          <Trash2 size={12} />
                        </button>
                      </div>
                      <div className="grid grid-cols-3 gap-2">
                        <input
                          data-testid={`stat-${i}-label`}
                          value={s.label}
                          onChange={(e) => {
                            const stats = [...config.settings.stats];
                            stats[i] = { ...stats[i], label: e.target.value };
                            updateSetting({ stats });
                          }}
                          placeholder="LABEL"
                          className="bg-black border border-[var(--line-strong)] focus:border-[var(--accent)] outline-none px-2 py-2 text-xs font-mono"
                        />
                        <input
                          data-testid={`stat-${i}-value`}
                          value={s.value}
                          onChange={(e) => {
                            const stats = [...config.settings.stats];
                            stats[i] = { ...stats[i], value: e.target.value };
                            updateSetting({ stats });
                          }}
                          placeholder="VALUE"
                          className="bg-black border border-[var(--line-strong)] focus:border-[var(--accent)] outline-none px-2 py-2 text-xs font-mono font-bold"
                        />
                        <input
                          data-testid={`stat-${i}-sublabel`}
                          value={s.sublabel || ""}
                          onChange={(e) => {
                            const stats = [...config.settings.stats];
                            stats[i] = { ...stats[i], sublabel: e.target.value };
                            updateSetting({ stats });
                          }}
                          placeholder="sublabel"
                          className="bg-black border border-[var(--line-strong)] focus:border-[var(--accent)] outline-none px-2 py-2 text-xs"
                        />
                      </div>
                    </div>
                  ))}
                </div>
                <p className="font-mono text-[10px] text-[var(--muted)] mt-3">
                  // last stat in the list renders as the highlighted lime card
                </p>
              </div>

              {config.settings.tebex_url && (
                <a
                  href={config.settings.tebex_url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="font-mono text-xs text-[var(--accent)] inline-flex items-center gap-2 hover:underline"
                >
                  <ExternalLink size={12} /> open tebex
                </a>
              )}
            </div>
          </section>
        )}

        {/* INTEGRATIONS: CFX + Tebex */}
        {tab === "integrations" && (
          <section className="space-y-8 max-w-4xl" data-testid="panel-integrations">
            {/* CFX Block */}
            <div className="bg-[#0a0a0a] border border-[var(--line-strong)] p-6 space-y-5">
              <div className="flex items-start justify-between gap-4 flex-wrap">
                <div>
                  <div className="bracket text-[var(--accent)]">[ CFX.RE LOGIN ]</div>
                  <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight mt-1">
                    FiveM / Cfx.re Account Login
                  </h2>
                </div>
                <button
                  data-testid="generate-cfx-keys"
                  onClick={regenCfxKeys}
                  className="btn-ghost !py-2 !px-3 !text-[11px]"
                >
                  <KeyRound size={12} /> GENERATE NEW KEYS
                </button>
              </div>

              <div className="border border-[var(--accent)]/20 bg-[var(--accent)]/5 p-4 font-mono text-[11px] text-[var(--muted)] leading-relaxed space-y-2">
                <div className="text-[var(--accent)] font-bold uppercase tracking-widest mb-1">// HOW IT WORKS</div>
                <p>
                  Cfx.re does NOT issue OAuth2 client IDs. Their official login flow is
                  <span className="text-white"> Discourse User API Keys</span> on forum.cfx.re.
                </p>
                <p>
                  Click <span className="text-[var(--accent)]">GENERATE NEW KEYS</span> below — we'll create an RSA keypair stored in the database and a unique
                  <span className="text-white"> Client ID</span> for your app. You don't need to register anything on Cfx.re.
                </p>
                <p>
                  When users click <span className="text-white">"CFX LOGIN"</span>, they'll be sent to forum.cfx.re to authorize your app. After approving,
                  they'll be redirected back to your store with their CFX identity.
                </p>
              </div>

              <Field
                label="CFX Forum Base URL"
                value={config.settings.cfx_forum_base_url || "https://forum.cfx.re"}
                onChange={(v) => updateSetting({ cfx_forum_base_url: v })}
                placeholder="https://forum.cfx.re"
                testid="cfx-forum-url"
              />
              <Field
                label="Application Name (shown to users on the approval screen)"
                value={config.settings.cfx_application_name || ""}
                onChange={(v) => updateSetting({ cfx_application_name: v })}
                placeholder="Null Modifications"
                testid="cfx-app-name"
                mono={false}
              />
              <Field
                label="CFX Client ID (auto-generated — internal label)"
                value={config.settings.cfx_client_id || ""}
                onChange={(v) => updateSetting({ cfx_client_id: v })}
                placeholder="(click GENERATE NEW KEYS above)"
                testid="cfx-client-id"
              />

              <div className="grid grid-cols-1 gap-3">
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] block mb-2">
                    &gt; CFX RSA Public Key (PEM) — sent to Cfx during login
                  </label>
                  <textarea
                    readOnly
                    value={config.settings.cfx_public_key_pem || "(empty — click GENERATE NEW KEYS)"}
                    rows={4}
                    data-testid="cfx-pub-key"
                    className="w-full bg-black border border-[var(--line-strong)] outline-none px-3 py-2 text-[10px] text-[var(--muted)] font-mono"
                  />
                </div>
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] block mb-2">
                    &gt; CFX RSA Private Key (PEM) — used to decrypt the CFX callback payload. <span className="text-red-500">Keep this secret.</span>
                  </label>
                  <textarea
                    readOnly
                    value={config.settings.cfx_private_key_pem ? "[REDACTED — present in DB]" : "(empty)"}
                    rows={2}
                    className="w-full bg-black border border-[var(--line-strong)] outline-none px-3 py-2 text-[10px] text-[var(--muted)] font-mono"
                  />
                </div>
              </div>
            </div>

            {/* Tebex Block */}
            <div className="bg-[#0a0a0a] border border-[var(--line-strong)] p-6 space-y-5">
              <div>
                <div className="bracket text-[var(--accent)]">[ TEBEX HEADLESS CHECKOUT ]</div>
                <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight mt-1">
                  Tebex Cart Checkout
                </h2>
              </div>

              <div className="border border-[var(--accent)]/20 bg-[var(--accent)]/5 p-4 font-mono text-[11px] text-[var(--muted)] leading-relaxed space-y-2">
                <div className="text-[var(--accent)] font-bold uppercase tracking-widest mb-1">// HOW TO GET YOUR KEYS</div>
                <p>1. Log into <a href="https://creator.tebex.io" target="_blank" rel="noopener noreferrer" className="text-[var(--accent)] underline">creator.tebex.io</a></p>
                <p>2. Go to <span className="text-white">Developers → API Keys</span></p>
                <p>3. Copy the <span className="text-white">Public Token</span> and <span className="text-white">Private Key</span> shown there.</p>
                <p>4. Set your <span className="text-white">Tebex Store URL</span> below (e.g. <span className="text-[var(--accent)]">https://null-modifications.tebex.io</span>).</p>
                <p>5. For each product on the <span className="text-[var(--accent)]">Products</span> tab, paste the <span className="text-white">full Tebex package URL</span> (e.g. <span className="text-[var(--accent)]">https://null-modifications.tebex.io/package/7449457</span>) — the system extracts the ID safely. A raw number works too.</p>
                <p className="pt-1">// Checkout flow: customer fills cart on YOUR site → clicks "CHECKOUT VIA TEBEX" → lands directly on the Tebex hosted basket with their items pre-loaded → pays.</p>
                <p className="pt-1 text-red-400">⚠ Private Key must NEVER be shared publicly. Stored encrypted in your DB.</p>
              </div>

              <Field
                label="Tebex Public Token (Webstore Identifier)"
                value={config.settings.tebex_public_token || ""}
                onChange={(v) => updateSetting({ tebex_public_token: v })}
                placeholder="e.g. abc123def456..."
                testid="tebex-public-token"
              />
              <Field
                label="Tebex Private Key (HTTP Basic password)"
                value={config.settings.tebex_private_key || ""}
                onChange={(v) => updateSetting({ tebex_private_key: v })}
                placeholder="e.g. 9f8e7d6c5b4a..."
                testid="tebex-private-key"
                type="password"
              />
              <Field
                label="Tebex Store URL (your public Tebex storefront)"
                value={config.settings.tebex_store_url || ""}
                onChange={(v) => updateSetting({ tebex_store_url: v })}
                placeholder="https://null-modifications.tebex.io"
                testid="tebex-store-url"
              />
              <p className="font-mono text-[10px] text-[var(--muted)] -mt-2">
                // example: <span className="text-white">https://null-modifications.tebex.io</span> — used as a fallback checkout URL if needed. No trailing slash.
              </p>

              {/* === WEBHOOK SETUP === */}
              <div className="border border-[var(--accent)]/20 bg-[var(--accent)]/5 p-4 font-mono text-[11px] text-[var(--muted)] leading-relaxed space-y-2 mt-6">
                <div className="text-[var(--accent)] font-bold uppercase tracking-widest mb-1">// WEBHOOK SETUP (FOR PAYMENT CONFIRMATION + CUSTOMER EMAIL)</div>
                <p>This lets Tebex notify your site the second a payment goes through — orders auto-update from <span className="text-orange-400">AWAITING_PAYMENT</span> → <span className="text-green-400">PAID</span> and the buyer's email + payment method show up in the Orders tab.</p>
                <ol className="list-decimal list-inside space-y-1 mt-2">
                  <li>Go to <a href="https://creator.tebex.io" target="_blank" rel="noopener noreferrer" className="text-[var(--accent)] underline">creator.tebex.io</a> → <span className="text-white">Integrations → Webhook Endpoints</span></li>
                  <li>Click <span className="text-white">Add Endpoint</span></li>
                  <li>Paste this URL: <code className="bg-black px-2 py-0.5 text-[var(--accent)] break-all">{(typeof window !== "undefined" ? window.location.origin : "https://nullmodifications.com")}/api/webhooks/tebex</code></li>
                  <li>Subscribe to events: <span className="text-white">payment.completed, payment.declined, payment.refunded, payment.chargeback</span></li>
                  <li>Tebex will give you a <span className="text-white">Webhook Secret</span> — paste it below and SAVE.</li>
                </ol>
                <p className="pt-1 text-red-400">⚠ Without the webhook secret, signature verification fails and orders won't update.</p>
              </div>

              <Field
                label="Tebex Webhook Secret (HMAC signing key)"
                value={config.settings.tebex_webhook_secret || ""}
                onChange={(v) => updateSetting({ tebex_webhook_secret: v })}
                placeholder="paste the secret Tebex gave you after creating the webhook endpoint"
                testid="tebex-webhook-secret"
                type="password"
              />

              <div>
                <label className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] block mb-2">
                  &gt; Store Type
                </label>
                <select
                  data-testid="tebex-store-type"
                  value={config.settings.tebex_store_type || "universal"}
                  onChange={(e) => updateSetting({ tebex_store_type: e.target.value })}
                  className="w-full bg-black border border-[var(--line-strong)] focus:border-[var(--accent)] outline-none px-3 py-2 text-sm text-white font-mono"
                >
                  <option value="universal">Universal — FiveM, Discord, generic (RECOMMENDED)</option>
                  <option value="fivem">FiveM (legacy username mode)</option>
                  <option value="minecraft">Minecraft / Rust (requires username at basket)</option>
                </select>
                <p className="font-mono text-[10px] text-[var(--muted)] mt-2">
                  // Default: Universal — works for FiveM/CFX stores. The CFX user is identified via custom metadata on the basket.<br />
                  // Only pick Minecraft if Tebex rejects checkout with "username is required".
                </p>
              </div>
            </div>
          </section>
        )}

        {/* ORDERS */}
        {tab === "orders" && (
          <section className="space-y-4" data-testid="panel-orders">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight">
                  Orders
                </h2>
                <p className="font-mono text-[10px] text-[var(--muted)] mt-1">
                  // last 200 baskets submitted to tebex.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid="sync-all-orders"
                  onClick={handleSyncAll}
                  disabled={syncingAll}
                  className="btn-ghost !py-2 !px-3 !text-[11px] disabled:opacity-50"
                  title="Re-check every open order against Tebex"
                >
                  <RefreshCw size={12} className={syncingAll ? "animate-spin" : ""} />
                  {syncingAll ? "SYNCING..." : "SYNC ALL FROM TEBEX"}
                </button>
                <button
                  data-testid="refresh-orders"
                  onClick={refreshOrders}
                  disabled={ordersLoading}
                  className="btn-ghost !py-2 !px-3 !text-[11px] disabled:opacity-50"
                >
                  <RefreshCw size={12} /> {ordersLoading ? "LOADING..." : "REFRESH"}
                </button>
              </div>
            </div>

            {orders.length === 0 ? (
              <div className="border border-dashed border-[var(--line-strong)] p-10 text-center font-mono text-xs text-[var(--muted)]">
                // no orders yet
              </div>
            ) : (
              <div className="border border-[var(--line-strong)] bg-[#0a0a0a] overflow-x-auto">
                <table className="w-full text-xs font-mono">
                  <thead className="bg-black/70 text-[var(--accent)] uppercase tracking-widest text-[10px]">
                    <tr>
                      <th className="text-left p-3">Date</th>
                      <th className="text-left p-3">CFX User</th>
                      <th className="text-left p-3">Items</th>
                      <th className="text-left p-3">Amount</th>
                      <th className="text-left p-3">Tx ID</th>
                      <th className="text-left p-3">Status</th>
                      <th className="text-left p-3"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {orders.map((o) => {
                      const status = o.status || "pending";
                      const statusColor = {
                        paid: "text-green-400 border-green-400/40 bg-green-400/10",
                        refunded: "text-orange-400 border-orange-400/40 bg-orange-400/10",
                        declined: "text-red-400 border-red-400/40 bg-red-400/10",
                        chargeback: "text-red-400 border-red-400/40 bg-red-400/10",
                        disputed: "text-red-400 border-red-400/40 bg-red-400/10",
                        cancelled: "text-[var(--muted)] border-[var(--line-strong)] bg-black/40",
                      }[status] || "text-[var(--accent)] border-[var(--accent)]/40 bg-[var(--accent)]/10";
                      const statusLabel = {
                        awaiting_payment: "HANDLED AT TEBEX",
                        pending: "INITIATED",
                        cancelled: "CANCELLED",
                      }[status] || status.replace(/_/g, " ");
                      const refreshing = !!refreshingOrders[o.id];
                      return (
                        <tr key={o.id} className="border-t border-[var(--line)]">
                          <td className="p-3 text-[var(--muted)] whitespace-nowrap">{o.created_at?.slice(0, 19).replace("T", " ")}</td>
                          <td className="p-3 text-white">{o.cfx_username || `#${o.cfx_forum_id}`}</td>
                          <td className="p-3 text-[var(--muted)]">
                            {(o.items || []).map((it) => `${it.product_id}×${it.quantity}`).join(", ")}
                          </td>
                          <td className="p-3 text-[var(--accent)]">{o.payment_amount || <span className="text-[var(--muted)]">—</span>}</td>
                          <td className="p-3 text-[var(--accent)] break-all max-w-[160px] truncate" title={o.tebex_transaction_id || o.tebex_basket_ident}>
                            {o.tebex_transaction_id || o.tebex_basket_ident?.slice(0, 12) + "…"}
                          </td>
                          <td className="p-3">
                            <span className={`border px-2 py-0.5 uppercase tracking-widest text-[10px] ${statusColor}`}>
                              {statusLabel}
                            </span>
                          </td>
                          <td className="p-3">
                            <div className="flex gap-1">
                              <button
                                data-testid={`refresh-order-${o.id}`}
                                onClick={() => refreshOneOrder(o.id)}
                                disabled={refreshing}
                                className="border border-[var(--line-strong)] hover:border-[var(--accent)] text-[var(--muted)] hover:text-[var(--accent)] px-2 py-1 font-mono text-[9px] uppercase tracking-widest disabled:opacity-40 flex items-center gap-1"
                                title="Pull live status from Tebex"
                              >
                                <RefreshCw size={9} className={refreshing ? "animate-spin" : ""} />
                                {refreshing ? "..." : "SYNC"}
                              </button>
                              <button
                                data-testid={`delete-order-${o.id}`}
                                onClick={() => deleteOneOrder(o.id)}
                                className="border border-[var(--line-strong)] hover:border-red-500 hover:text-red-500 text-[var(--muted)] p-1"
                                title="Remove order from dashboard"
                              >
                                <Trash2 size={10} />
                              </button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </section>
        )}

        {/* TIERS */}
        {tab === "tiers" && (
          <section className="space-y-6" data-testid="panel-tiers">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight">
                  Pricing Tiers
                </h2>
                <p className="font-mono text-[10px] text-[var(--muted)] mt-1">
                  // info-only price sheet shown above the storefront — no clickable buttons.
                </p>
              </div>
              <button
                data-testid="add-tier"
                onClick={() => {
                  const tiers = [...config.tiers, {
                    id: `tier-${Date.now()}`,
                    name: "New Tier",
                    price: "$0.00",
                    unit: "Per Vehicle",
                    description: "",
                    features: [],
                    cta: "tebex",
                    order: config.tiers.length + 1,
                  }];
                  setConfig({ ...config, tiers });
                }}
                className="btn-accent !py-2 !px-3 !text-[11px]"
              >
                <Plus size={12} /> ADD TIER
              </button>
            </div>
            {config.tiers.map((t, idx) => (
              <div key={t.id} className="bg-[#0a0a0a] border border-[var(--line-strong)] p-6 space-y-4" data-testid={`tier-editor-${t.id}`}>
                <div className="flex items-center justify-between">
                  <div className="bracket text-[var(--accent)]">[ TIER #{String(idx + 1).padStart(2, "0")} — {t.id.toUpperCase()} ]</div>
                  <button
                    onClick={() => {
                      if (!window.confirm(`Delete tier "${t.name}"?`)) return;
                      const tiers = config.tiers.filter((_, i) => i !== idx);
                      setConfig({ ...config, tiers });
                    }}
                    className="border border-[var(--line-strong)] hover:border-red-500 hover:text-red-500 p-2"
                    aria-label="delete tier"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <Field label="Name" value={t.name} onChange={(v) => updateTier(idx, { name: v })} testid={`tier-${t.id}-name`} mono={false} />
                  <Field label="Price" value={t.price} onChange={(v) => updateTier(idx, { price: v })} testid={`tier-${t.id}-price`} />
                  <Field label="Unit / Subtitle" value={t.unit} onChange={(v) => updateTier(idx, { unit: v })} testid={`tier-${t.id}-unit`} />
                </div>
                <Field
                  label="Description"
                  value={t.description}
                  onChange={(v) => updateTier(idx, { description: v })}
                  type="textarea"
                  testid={`tier-${t.id}-desc`}
                  mono={false}
                />

                <div>
                  <div className="flex items-center justify-between mb-3">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">
                      &gt; features
                    </div>
                    <button
                      data-testid={`add-feature-${t.id}`}
                      onClick={() => addFeature(idx)}
                      className="font-mono text-[10px] uppercase tracking-widest text-[var(--accent)] hover:underline flex items-center gap-1"
                    >
                      <Plus size={10} /> ADD
                    </button>
                  </div>
                  <div className="space-y-2">
                    {t.features.map((f, fi) => (
                      <div key={fi} className="flex items-center gap-2">
                        <select
                          value={f.ok ? "yes" : "no"}
                          onChange={(e) => updateFeature(idx, fi, { ok: e.target.value === "yes" })}
                          className="bg-black border border-[var(--line-strong)] px-2 py-2 text-xs font-mono"
                        >
                          <option value="yes">✓ YES</option>
                          <option value="no">✗ NO</option>
                        </select>
                        <input
                          value={f.text}
                          onChange={(e) => updateFeature(idx, fi, { text: e.target.value })}
                          className="flex-1 bg-black border border-[var(--line-strong)] focus:border-[var(--accent)] outline-none px-3 py-2 text-sm"
                        />
                        <button
                          onClick={() => removeFeature(idx, fi)}
                          className="border border-[var(--line-strong)] hover:border-red-500 hover:text-red-500 p-2"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </section>
        )}

        {/* BULK & ADD-ONS panels removed — replaced by Products */}
        {tab === "products" && (
          <section className="space-y-6" data-testid="panel-products">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight">
                  Products / Storefront
                </h2>
                <p className="font-mono text-[10px] text-[var(--muted)] mt-1">
                  // categories appear as tabs on the landing page. empty ones show a "Coming Soon" led sign.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <button
                  data-testid="sync-tebex-btn"
                  onClick={openTebexSync}
                  className="btn-ghost !py-2 !px-3 !text-[11px]"
                  title="Pull all packages from your connected Tebex storefront"
                >
                  <RefreshCw size={12} /> SYNC FROM TEBEX
                </button>
                <button
                  data-testid="add-product-category"
                  onClick={addProductCategory}
                  className="btn-accent !py-2 !px-3 !text-[11px]"
                >
                  <Plus size={12} /> ADD CATEGORY
                </button>
              </div>
            </div>

            {product_categories.length === 0 && (
              <div className="border border-dashed border-[var(--line-strong)] p-10 text-center font-mono text-xs text-[var(--muted)]">
                // no categories — click "ADD CATEGORY" to start
              </div>
            )}

            {product_categories.map((cat, catIdx) => (
              <div
                key={cat.id}
                data-testid={`pcat-${cat.id}`}
                className="bg-[#0a0a0a] border border-[var(--line-strong)] p-6 space-y-4"
              >
                <div className="flex items-center justify-between gap-3 flex-wrap">
                  <div className="bracket text-[var(--accent)]">
                    [ CATEGORY #{String(catIdx + 1).padStart(2, "0")} ]
                  </div>
                  <div className="flex items-center gap-1">
                    <button onClick={() => moveProductCategory(catIdx, -1)} disabled={catIdx === 0}
                            className="border border-[var(--line-strong)] hover:border-[var(--accent)] px-2 py-1 text-xs font-mono disabled:opacity-30">↑</button>
                    <button onClick={() => moveProductCategory(catIdx, +1)} disabled={catIdx === product_categories.length - 1}
                            className="border border-[var(--line-strong)] hover:border-[var(--accent)] px-2 py-1 text-xs font-mono disabled:opacity-30">↓</button>
                    <button onClick={() => removeProductCategory(catIdx)}
                            className="border border-[var(--line-strong)] hover:border-red-500 hover:text-red-500 p-2 ml-2">
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field
                    label="Category name (e.g. Vehicles)"
                    value={cat.name}
                    onChange={(v) => updateProductCategory(catIdx, { name: v })}
                    mono={false}
                  />
                  <Field
                    label="Short description"
                    value={cat.description || ""}
                    onChange={(v) => updateProductCategory(catIdx, { description: v })}
                    mono={false}
                  />
                </div>

                {/* Product list within this category */}
                <div className="pt-4 border-t border-[var(--line)]">
                  <div className="flex items-center justify-between mb-4">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">
                      &gt; products ({cat.products?.length || 0}) {(!cat.products || cat.products.length === 0) && <span className="text-[var(--accent)]">— shows COMING SOON sign</span>}
                    </div>
                    <button
                      data-testid={`add-product-${cat.id}`}
                      onClick={() => addProduct(catIdx)}
                      className="font-mono text-[10px] uppercase tracking-widest text-[var(--accent)] hover:underline flex items-center gap-1"
                    >
                      <Plus size={10} /> ADD PRODUCT
                    </button>
                  </div>

                  <div className="space-y-3">
                    {(cat.products || []).map((prod, prodIdx) => (
                      <div key={prod.id}
                           data-testid={`prod-${prod.id}`}
                           className="border border-[var(--line-strong)] p-4 space-y-3 bg-black/40">
                        <div className="flex items-center justify-between">
                          <div className="font-mono text-[10px] text-[var(--accent)] tracking-widest">
                            PRODUCT #{String(prodIdx + 1).padStart(2, "0")}
                          </div>
                          <button onClick={() => removeProduct(catIdx, prodIdx)}
                                  className="border border-[var(--line-strong)] hover:border-red-500 hover:text-red-500 p-1.5">
                            <Trash2 size={12} />
                          </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                          <Field
                            label="Title"
                            value={prod.title}
                            onChange={(v) => updateProduct(catIdx, prodIdx, { title: v })}
                            mono={false}
                          />
                          <Field
                            label="Price (optional, e.g. $45.00)"
                            value={prod.price || ""}
                            onChange={(v) => updateProduct(catIdx, prodIdx, { price: v })}
                          />
                        </div>
                        <Field
                          label="Tebex purchase URL (fallback for items without a Tebex package_id)"
                          value={prod.purchase_url || ""}
                          onChange={(v) => updateProduct(catIdx, prodIdx, { purchase_url: v })}
                          placeholder="https://yourshop.tebex.io/package/..."
                        />
                        <Field
                          label="Tebex Package URL or ID (enables cart checkout)"
                          value={prod.tebex_package_id || ""}
                          onChange={(v) => updateProduct(catIdx, prodIdx, { tebex_package_id: v })}
                          placeholder="https://null-modifications.tebex.io/package/7449457  (or just  7449457)"
                          testid={`prod-${prod.id}-tebex-pkg`}
                        />

                        <div>
                          <label className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] block mb-2">
                            &gt; Cart Thumbnail (shown in cart drawer — recommended if your product image is a video)
                          </label>
                          <ImageUploadInput
                            value={prod.cart_thumbnail || ""}
                            onChange={(v) => updateProduct(catIdx, prodIdx, { cart_thumbnail: v })}
                            accept="image/*"
                            placeholder="Optional. Leave blank to auto-pick the first image. Falls back to your site logo."
                            testid={`prod-${prod.id}-cart-thumb`}
                          />
                        </div>
                        <div>
                          <label className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] block mb-2">
                            &gt; Website Link Thumbnail (shown as the preview image on Discord / Twitter / Slack when you share the product link)
                          </label>
                          <ImageUploadInput
                            value={prod.share_thumbnail || ""}
                            onChange={(v) => updateProduct(catIdx, prodIdx, { share_thumbnail: v })}
                            accept="image/*"
                            placeholder="Direct image URL (Imgur/Postimg). Recommended size: 1200×630. Falls back to Cart Thumbnail."
                            testid={`prod-${prod.id}-share-thumb`}
                          />
                          <p className="font-mono text-[10px] text-[var(--muted)] mt-1 leading-relaxed">
                            // for best results use a wide image (1200×630 px) hosted on a direct image URL.
                            this is what Discord shows when someone pastes the product's share link.
                          </p>
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-[1fr_180px] gap-3">
                          <Field
                            label="Description"
                            type="textarea"
                            value={prod.description || ""}
                            onChange={(v) => updateProduct(catIdx, prodIdx, { description: v })}
                            mono={false}
                          />
                          <Field
                            label="Badge (optional, e.g. NEW)"
                            value={prod.badge || ""}
                            onChange={(v) => updateProduct(catIdx, prodIdx, { badge: v })}
                          />
                        </div>

                        {/* Flash Sale controls */}
                        <div className="grid grid-cols-1 md:grid-cols-[140px_1fr] gap-3 bg-red-500/5 border border-red-500/30 p-3">
                          <div>
                            <label className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] block mb-2">
                              &gt; flash sale %
                            </label>
                            <input
                              data-testid={`prod-${prod.id}-discount`}
                              type="number"
                              min="0"
                              max="99"
                              value={prod.discount ?? 0}
                              onChange={(e) => {
                                const v = Math.max(0, Math.min(99, parseInt(e.target.value, 10) || 0));
                                updateProduct(catIdx, prodIdx, { discount: v });
                              }}
                              className="w-full bg-black border border-[var(--line-strong)] focus:border-[var(--accent)] outline-none px-3 py-2 text-lg font-mono font-bold text-[var(--accent)]"
                            />
                          </div>
                          <Field
                            label="Sale badge text (optional — defaults to -X% SALE)"
                            value={prod.sale_label || ""}
                            onChange={(v) => updateProduct(catIdx, prodIdx, { sale_label: v })}
                            placeholder="FLASH SALE / WEEKEND ONLY / etc."
                          />
                          {prod.discount > 0 && prod.price && (
                            <div className="md:col-span-2 font-mono text-[10px] text-[var(--muted)] tracking-widest">
                              // PREVIEW: {prod.price} → <span className="text-[var(--accent)] font-bold">{applyDiscountPreview(prod.price, prod.discount)}</span>
                            </div>
                          )}
                        </div>

                        {/* Images */}
                        <div className="pt-3 border-t border-[var(--line)]">
                          <div className="flex items-center justify-between mb-2">
                            <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">
                              &gt; images ({prod.images?.length || 0}) — first image = thumbnail
                            </div>
                            <button
                              onClick={() => addProductImage(catIdx, prodIdx)}
                              className="font-mono text-[10px] uppercase tracking-widest text-[var(--accent)] hover:underline flex items-center gap-1"
                            >
                              <Plus size={10} /> ADD IMAGE
                            </button>
                          </div>
                          <div className="space-y-2">
                            {(prod.images || []).map((img, ii) => (
                              <div key={img.id} className="grid grid-cols-[48px_1fr_auto_auto] gap-2 items-center">
                                <div className="aspect-square bg-black border border-[var(--line-strong)] overflow-hidden">
                                  {img.url ? (
                                    <img src={img.url} alt="" className="w-full h-full object-cover" />
                                  ) : (
                                    <div className="w-full h-full flex items-center justify-center font-mono text-[10px] text-[var(--muted)]">
                                      {String(ii + 1).padStart(2, "0")}
                                    </div>
                                  )}
                                </div>
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                  <input
                                    value={img.url}
                                    onChange={(e) => updateProductImage(catIdx, prodIdx, ii, { url: e.target.value })}
                                    placeholder="paste URL or upload →"
                                    className="bg-black border border-[var(--line-strong)] focus:border-[var(--accent)] outline-none px-2 py-2 text-xs font-mono"
                                  />
                                  <input
                                    value={img.caption || ""}
                                    onChange={(e) => updateProductImage(catIdx, prodIdx, ii, { caption: e.target.value })}
                                    placeholder="caption (optional)"
                                    className="bg-black border border-[var(--line-strong)] focus:border-[var(--accent)] outline-none px-2 py-2 text-xs"
                                  />
                                </div>
                                <ImageUploadInput
                                  value={img.url || ""}
                                  onChange={(v) => updateProductImage(catIdx, prodIdx, ii, { url: v })}
                                  accept="image/*,video/mp4,video/webm,video/quicktime"
                                  compact
                                  testid={`prod-${prod.id}-img-${ii}-upload`}
                                />
                                <button
                                  onClick={() => removeProductImage(catIdx, prodIdx, ii)}
                                  className="border border-[var(--line-strong)] hover:border-red-500 hover:text-red-500 p-2"
                                >
                                  <Trash2 size={14} />
                                </button>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </section>
        )}

        {/* PORTFOLIO */}
        {tab === "portfolio" && (
          <section className="space-y-6" data-testid="panel-portfolio">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div>
                <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight">
                  Portfolio
                </h2>
                <p className="font-mono text-[10px] text-[var(--muted)] mt-1">
                  // each card = one car / build. paste image URLs from imgur, your CDN, discord, etc.
                </p>
              </div>
              <button
                data-testid="add-category"
                onClick={addCategory}
                className="btn-accent !py-2 !px-3 !text-[11px]"
              >
                <Plus size={12} /> ADD BUILD
              </button>
            </div>

            {portfolio.length === 0 && (
              <div className="border border-dashed border-[var(--line-strong)] p-10 text-center font-mono text-xs text-[var(--muted)]">
                // no portfolio entries yet — click "ADD BUILD" to start
              </div>
            )}

            {portfolio.map((cat, idx) => (
              <div
                key={cat.id}
                data-testid={`category-editor-${cat.id}`}
                className="bg-[#0a0a0a] border border-[var(--line-strong)] p-6 space-y-4"
              >
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="bracket text-[var(--accent)]">
                    [ BUILD #{String(idx + 1).padStart(2, "0")} ]
                  </div>
                  <div className="flex items-center gap-1">
                    <button
                      onClick={() => moveCategory(idx, -1)}
                      disabled={idx === 0}
                      className="border border-[var(--line-strong)] hover:border-[var(--accent)] px-2 py-1 text-xs font-mono disabled:opacity-30"
                      aria-label="move up"
                    >↑</button>
                    <button
                      onClick={() => moveCategory(idx, +1)}
                      disabled={idx === portfolio.length - 1}
                      className="border border-[var(--line-strong)] hover:border-[var(--accent)] px-2 py-1 text-xs font-mono disabled:opacity-30"
                      aria-label="move down"
                    >↓</button>
                    <button
                      onClick={() => removeCategory(idx)}
                      className="border border-[var(--line-strong)] hover:border-red-500 hover:text-red-500 p-2 ml-2"
                      aria-label="delete"
                    >
                      <Trash2 size={14} />
                    </button>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Field
                    label="Build name"
                    value={cat.name}
                    onChange={(v) => updateCategory(idx, { name: v })}
                    testid={`category-${cat.id}-name`}
                    mono={false}
                  />
                  <div>
                    <label className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] block mb-2">
                      &gt; Thumbnail (the cover image)
                    </label>
                    <ImageUploadInput
                      value={cat.thumbnail || ""}
                      onChange={(v) => updateCategory(idx, { thumbnail: v })}
                      testid={`category-${cat.id}-thumb`}
                    />
                  </div>
                </div>
                <Field
                  label="Description"
                  value={cat.description || ""}
                  onChange={(v) => updateCategory(idx, { description: v })}
                  type="textarea"
                  testid={`category-${cat.id}-desc`}
                  mono={false}
                />

                {/* Not For Sale toggle — for privately commissioned builds */}
                <label
                  className={`flex items-start gap-3 p-3 cursor-pointer border transition select-none ${
                    cat.not_for_sale
                      ? "border-red-500/70 bg-red-500/5"
                      : "border-[var(--line-strong)] hover:border-[var(--accent)]/40"
                  }`}
                >
                  <input
                    type="checkbox"
                    data-testid={`category-${cat.id}-not-for-sale`}
                    checked={!!cat.not_for_sale}
                    onChange={(e) => updateCategory(idx, { not_for_sale: e.target.checked })}
                    className="mt-1 w-4 h-4 accent-red-500 cursor-pointer"
                  />
                  <div className="flex-1">
                    <div className={`font-mono text-xs uppercase tracking-widest font-bold ${cat.not_for_sale ? "text-red-400" : "text-white"}`}>
                      NOT FOR SALE — Private Commission
                    </div>
                    <div className="font-mono text-[10px] text-[var(--muted)] mt-1">
                      // when ticked, a pulsing red "NOT FOR SALE" badge appears on this archive entry so visitors know it was a private build and isn't available for purchase.
                    </div>
                  </div>
                  {cat.not_for_sale && (
                    <span className="nfs-badge flex-shrink-0">NOT FOR SALE</span>
                  )}
                </label>

                {/* Giveaway Winner toggle — for portfolio entries that were awarded as raffle prizes */}
                <label
                  className={`flex items-start gap-3 p-3 cursor-pointer border transition select-none ${
                    cat.giveaway_winner
                      ? "border-yellow-500/70 bg-yellow-500/5"
                      : "border-[var(--line-strong)] hover:border-yellow-500/40"
                  }`}
                >
                  <input
                    type="checkbox"
                    data-testid={`category-${cat.id}-giveaway-winner`}
                    checked={!!cat.giveaway_winner}
                    onChange={(e) => updateCategory(idx, { giveaway_winner: e.target.checked })}
                    className="mt-1 w-4 h-4 accent-yellow-500 cursor-pointer"
                  />
                  <div className="flex-1">
                    <div className={`font-mono text-xs uppercase tracking-widest font-bold ${cat.giveaway_winner ? "text-yellow-400" : "text-white"}`}>
                      GIVEAWAY WINNER — Raffle Prize
                    </div>
                    <div className="font-mono text-[10px] text-[var(--muted)] mt-1">
                      // when ticked, a gold pulsing &quot;GIVEAWAY WINNER&quot; badge with animated shimmer appears on this build. Optionally add the winner&apos;s name below.
                    </div>
                  </div>
                  {cat.giveaway_winner && (
                    <span className="gw-badge flex-shrink-0">GIVEAWAY WINNER</span>
                  )}
                </label>
                {cat.giveaway_winner && (
                  <Field
                    label="Winner name (optional — displayed alongside the badge)"
                    value={cat.giveaway_winner_name || ""}
                    onChange={(v) => updateCategory(idx, { giveaway_winner_name: v })}
                    placeholder="e.g. JOESHMOE"
                    testid={`category-${cat.id}-giveaway-winner-name`}
                    mono={false}
                  />
                )}

                {/* External download URL (GTA5-Mods, etc.) */}
                <Field
                  label="External Download URL (optional — GTA5-Mods, LCPDFR, etc.)"
                  value={cat.external_url || ""}
                  onChange={(v) => updateCategory(idx, { external_url: v })}
                  placeholder="https://www.gta5-mods.com/vehicles/..."
                  testid={`category-${cat.id}-external-url`}
                  mono={false}
                />
                <p className="font-mono text-[10px] text-[var(--muted)] -mt-2">
                  // when set, a download icon appears at the top of this build's lightbox linking visitors out to download it.
                </p>

                {/* Website Link Thumbnail (Open Graph image for Discord/Twitter previews) */}
                <div>
                  <label className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)] block mb-2">
                    &gt; Website Link Thumbnail (shown as the preview image on Discord / Twitter / Slack when you share this build's link)
                  </label>
                  <ImageUploadInput
                    value={cat.share_thumbnail || ""}
                    onChange={(v) => updateCategory(idx, { share_thumbnail: v })}
                    accept="image/*"
                    placeholder="Direct image URL (Imgur/Postimg). Recommended size: 1200×630. Falls back to the main thumbnail."
                    testid={`category-${cat.id}-share-thumb`}
                  />
                  <p className="font-mono text-[10px] text-[var(--muted)] mt-1 leading-relaxed">
                    // for best results use a wide image (1200×630 px) hosted on a direct image URL.
                    this is what Discord shows when someone pastes the build's share link.
                  </p>
                </div>

                {cat.thumbnail && (
                  <div className="aspect-[4/3] max-w-[280px] border border-[var(--line-strong)] overflow-hidden">
                    <img src={cat.thumbnail} alt="thumbnail" className="w-full h-full object-cover" />
                  </div>
                )}

                {/* Images list */}
                <div className="pt-4 border-t border-[var(--line)]">
                  <div className="flex items-center justify-between mb-3">
                    <div className="font-mono text-[10px] uppercase tracking-widest text-[var(--muted)]">
                      &gt; images ({cat.images?.length || 0})
                    </div>
                    <button
                      data-testid={`add-image-${cat.id}`}
                      onClick={() => addImageToCategory(idx)}
                      className="font-mono text-[10px] uppercase tracking-widest text-[var(--accent)] hover:underline flex items-center gap-1"
                    >
                      <Plus size={10} /> ADD IMAGE
                    </button>
                  </div>

                  <div className="space-y-2">
                    {(cat.images || []).map((img, ii) => (
                      <div
                        key={img.id}
                        className="grid grid-cols-[60px_1fr_auto_auto] gap-2 items-center"
                      >
                        <div className="aspect-square bg-black border border-[var(--line-strong)] overflow-hidden">
                          {img.url ? (
                            <img src={img.url} alt="" className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center font-mono text-[10px] text-[var(--muted)]">
                              {String(ii + 1).padStart(2, "0")}
                            </div>
                          )}
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                          <input
                            value={img.url}
                            onChange={(e) => updateImage(idx, ii, { url: e.target.value })}
                            placeholder="paste URL or upload →"
                            className="bg-black border border-[var(--line-strong)] focus:border-[var(--accent)] outline-none px-2 py-2 text-xs font-mono"
                          />
                          <input
                            value={img.caption || ""}
                            onChange={(e) => updateImage(idx, ii, { caption: e.target.value })}
                            placeholder="caption (optional)"
                            className="bg-black border border-[var(--line-strong)] focus:border-[var(--accent)] outline-none px-2 py-2 text-xs"
                          />
                        </div>
                        <ImageUploadInput
                          value={img.url || ""}
                          onChange={(v) => updateImage(idx, ii, { url: v })}
                          accept="image/*,video/mp4,video/webm,video/quicktime"
                          compact
                          testid={`portfolio-${cat.id}-img-${ii}-upload`}
                        />
                        <button
                          onClick={() => removeImage(idx, ii)}
                          className="border border-[var(--line-strong)] hover:border-red-500 hover:text-red-500 p-2"
                          aria-label="remove image"
                        >
                          <Trash2 size={14} />
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </section>
        )}

        {/* TOS */}
        {tab === "theme" && (
          <section className="space-y-8 max-w-6xl" data-testid="panel-theme">
            <div>
              <div className="bracket flex items-center gap-2 mb-2">
                <Palette size={12} /> [ THEME_ENGINE ]
              </div>
              <h2 className="font-display text-3xl md:text-4xl font-extrabold uppercase tracking-tight">
                Theme & Holiday Presets
              </h2>
              <p className="mt-3 text-[var(--theme-text-muted)] text-sm max-w-2xl leading-relaxed">
                Pick a preset to apply it site-wide. Edit any preset's colours below and hit <span className="text-[var(--theme-accent-primary)]">SAVE</span> in the header to push the change to all visitors. Built-in presets (Default, Christmas, Halloween, 4th of July) can be edited but not deleted.
              </p>
            </div>

            {/* Preset cards grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {themePresets.map((p) => {
                const isActive = p.id === activeThemeId;
                return (
                  <div
                    key={p.id}
                    data-testid={`theme-preset-${p.id}`}
                    className={`relative border bg-[var(--theme-surface)] p-5 transition ${
                      isActive
                        ? "border-[var(--theme-accent-primary)]"
                        : "border-[var(--theme-border)] hover:border-[rgba(var(--theme-accent-primary-rgb),0.5)]"
                    }`}
                  >
                    {isActive && (
                      <span className="absolute -top-2 left-4 bg-[var(--theme-accent-primary)] text-black font-mono text-[9px] tracking-[0.25em] px-2 py-0.5 uppercase">
                        <Check size={10} className="inline -mt-0.5 mr-1" /> Active
                      </span>
                    )}
                    <div className="flex items-center justify-between gap-3 mb-4">
                      <input
                        data-testid={`theme-preset-name-${p.id}`}
                        value={p.name || ""}
                        onChange={(e) => updatePreset(p.id, { name: e.target.value })}
                        disabled={p.locked}
                        className="flex-1 bg-transparent border-b border-[var(--theme-border)] focus:border-[var(--theme-accent-primary)] outline-none font-display font-bold text-lg tracking-tight disabled:opacity-70"
                      />
                      {p.locked && (
                        <span className="font-mono text-[9px] tracking-widest text-[var(--theme-text-muted)] uppercase">
                          built-in
                        </span>
                      )}
                    </div>

                    {/* Swatch row */}
                    <div className="flex items-center gap-2 mb-5">
                      <SwatchInput label="BG" value={p.bg} onChange={(v) => updatePreset(p.id, { bg: v })} testid={`theme-${p.id}-bg`} />
                      <SwatchInput label="Surface" value={p.surface} onChange={(v) => updatePreset(p.id, { surface: v })} testid={`theme-${p.id}-surface`} />
                      <SwatchInput label="Primary" value={p.accent_primary} onChange={(v) => updatePreset(p.id, { accent_primary: v })} testid={`theme-${p.id}-primary`} />
                      <SwatchInput label="Secondary" value={p.accent_secondary} onChange={(v) => updatePreset(p.id, { accent_secondary: v })} testid={`theme-${p.id}-secondary`} />
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        data-testid={`theme-activate-${p.id}`}
                        onClick={() => activatePreset(p.id)}
                        disabled={isActive}
                        className={`flex-1 font-mono text-[10px] uppercase tracking-widest px-3 py-2 border transition ${
                          isActive
                            ? "border-[var(--theme-accent-primary)] text-[var(--theme-accent-primary)] opacity-50 cursor-default"
                            : "border-[var(--theme-border)] text-[var(--theme-text)] hover:bg-[var(--theme-accent-primary)] hover:text-black hover:border-[var(--theme-accent-primary)]"
                        }`}
                      >
                        {isActive ? "Active" : "Activate"}
                      </button>
                      <button
                        data-testid={`theme-delete-${p.id}`}
                        onClick={() => deletePreset(p.id)}
                        disabled={p.locked}
                        title={p.locked ? "Built-in preset" : "Delete preset"}
                        className="p-2 border border-[var(--theme-border)] text-[var(--theme-text-muted)] hover:border-red-500 hover:text-red-500 transition disabled:opacity-30 disabled:cursor-not-allowed"
                      >
                        <Trash2 size={14} />
                      </button>
                    </div>
                  </div>
                );
              })}

              {/* Add new */}
              <button
                data-testid="theme-add-preset"
                onClick={addPreset}
                className="border border-dashed border-[var(--theme-border)] hover:border-[var(--theme-accent-primary)] hover:text-[var(--theme-accent-primary)] text-[var(--theme-text-muted)] p-5 min-h-[180px] flex flex-col items-center justify-center gap-2 transition group"
              >
                <Plus size={32} className="group-hover:scale-110 transition-transform" />
                <span className="font-mono text-[10px] uppercase tracking-widest">Add Custom Preset</span>
              </button>
            </div>

            <div className="border border-[var(--theme-border)] bg-[var(--theme-surface)] p-4 font-mono text-[11px] text-[var(--theme-text-muted)] leading-relaxed">
              <div className="text-[var(--theme-accent-primary)] font-bold uppercase tracking-widest mb-1">// HOW IT WORKS</div>
              Changes preview live in this dashboard. Hit <span className="text-[var(--theme-accent-primary)]">SAVE</span> in the top-right header to persist the active preset to the database — all site visitors will then see your new theme on their next page load.
            </div>
          </section>
        )}

        {tab === "raffle" && (
          <AdminRaffleTab config={config} updateSetting={updateSetting} />
        )}

        {tab === "tos" && (
          <section className="space-y-4 max-w-3xl" data-testid="panel-tos">
            <h2 className="font-display text-2xl font-extrabold uppercase tracking-tight mb-2">
              Terms of Service
            </h2>
            {config.settings.tos.map((line, i) => (
              <div key={i} className="flex gap-3 items-start">
                <span className="font-mono text-[var(--accent)] text-sm pt-3">0{i + 1}.</span>
                <textarea
                  value={line}
                  onChange={(e) => {
                    const tos = [...config.settings.tos];
                    tos[i] = e.target.value;
                    updateSetting({ tos });
                  }}
                  rows={2}
                  className="flex-1 bg-black border border-[var(--line-strong)] focus:border-[var(--accent)] outline-none px-3 py-2 text-sm"
                />
                <button
                  onClick={() => {
                    const tos = config.settings.tos.filter((_, k) => k !== i);
                    updateSetting({ tos });
                  }}
                  className="border border-[var(--line-strong)] hover:border-red-500 hover:text-red-500 p-2"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
            <button
              onClick={() => updateSetting({ tos: [...config.settings.tos, "New term"] })}
              className="btn-ghost !py-2 !px-3 !text-[11px]"
            >
              <Plus size={12} /> ADD TERM
            </button>
          </section>
        )}

        {/* Reset */}
        <div className="mt-16 pt-8 border-t border-[var(--line)] flex items-center justify-between gap-4">
          <p className="font-mono text-[10px] text-[var(--muted)] uppercase tracking-widest">
            // danger zone — restore factory defaults
          </p>
          <button
            data-testid="reset-btn"
            onClick={reset}
            className="border border-red-500/40 text-red-400 hover:bg-red-500 hover:text-black px-4 py-2 font-mono text-xs uppercase tracking-widest transition flex items-center gap-2"
          >
            <RotateCcw size={12} /> RESET TO DEFAULTS
          </button>
        </div>
      </main>

      {/* Tebex storefront sync modal */}
      <TebexSyncModal
        open={tebexSyncOpen}
        onClose={() => setTebexSyncOpen(false)}
        categories={product_categories}
        onImport={importTebexPackages}
      />

      {/* Latest Release: single-package Tebex picker */}
      <LatestReleaseTebexPicker
        open={latestReleasePickerOpen}
        onClose={() => setLatestReleasePickerOpen(false)}
        currentId={config.settings.latest_release?.tebex_package_id || ""}
        onPick={(pkg) => {
          const lr = config.settings.latest_release || {};
          updateSetting({
            latest_release: {
              ...lr,
              tebex_package_id: String(pkg.id),
              // Only auto-fill empty fields so we don't overwrite the admin's edits
              title: lr.title || pkg.name || "",
              description: lr.description || pkg.description || "",
              image: lr.image || pkg.image || "",
              platform: lr.platform || "TEBEX",
            },
          });
          toast.success(`Linked Tebex package: ${pkg.name || pkg.id}`);
        }}
      />
    </div>
  );
}
